import React, { useEffect, useState, useCallback } from "react";
import {
  Mail,
  Send,
  Bell,
  Save,
  ShieldAlert,
  Info,
  MessageCircle,
  Phone,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchAlertSettings,
  updateAlertSettings,
  testEmailAlert,
  testTelegramAlert,
  testWhatsappAlert,
} from "../../api/alerts";
import { useAuth } from "../../auth/AuthContext";

/* ── Custom Toggle Switch ───────────────────────────────────────────── */
function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`alrt-toggle${checked ? " alrt-toggle-on" : ""}`}
      type="button"
    >
      <span className="alrt-toggle-knob" />
    </button>
  );
}

/* ── Styled Input ───────────────────────────────────────────────────── */
function StyledInput({
  placeholder,
  value,
  onChange,
  type = "text",
  icon,
  mono = false,
}: {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  icon?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="alrt-input-wrap">
      {icon && <span className="alrt-input-icon">{icon}</span>}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`alrt-input${icon ? " alrt-input-has-icon" : ""}${mono ? " alrt-input-mono" : ""}`}
      />
    </div>
  );
}

/* ── Channel Card ───────────────────────────────────────────────────── */
function ChannelCard({
  title,
  subtitle,
  enabled,
  onToggle,
  accentColor,
  icon,
  children,
}: {
  title: string;
  subtitle: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  accentColor: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const watermark = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ size?: number; style?: React.CSSProperties }>, {
        size: 160,
        style: {
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity: 0.12,
          pointerEvents: "none",
          color: accentColor,
          zIndex: 0,
        }
      })
    : null;

  return (
    <div className={`alrt-card${!enabled ? " alrt-card-disabled" : ""}`} style={{ position: "relative", overflow: "hidden" }}>
      {watermark}

      {/* Card Header */}
      <div className="alrt-card-header" style={{ position: "relative", zIndex: 1 }}>
        <div className="alrt-card-title-row">
          <div
            className="alrt-channel-icon"
            style={{ background: accentColor + "22", color: accentColor }}
          >
            {icon}
          </div>
          <div>
            <div className="alrt-card-title">{title}</div>
            <div className="alrt-card-sub">{subtitle}</div>
          </div>
        </div>
        <div className="alrt-toggle-row">
          {!enabled && (
            <span className="alrt-disabled-pill">Disabled</span>
          )}
          <Toggle checked={enabled} onChange={onToggle} />
        </div>
      </div>

      {/* Card Body */}
      <div className={`alrt-card-body${!enabled ? " alrt-body-locked" : ""}`} style={{ position: "relative", zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */
export default function AlertsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramChatId, setTelegramChatId] = useState("");
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");

  const loadSettings = useCallback(async () => {
    try {
      const settings = await fetchAlertSettings();
      setEmailEnabled(settings.email_alerts_enabled);
      setTelegramEnabled(settings.telegram_alerts_enabled);
      setTelegramChatId(settings.telegram_chat_id || "");
      setWhatsappEnabled(settings.whatsapp_alerts_enabled);
      setWhatsappNumber(settings.whatsapp_number || "");
      if (user?.email) setTestEmailAddress(user.email);
    } catch {
      toast.error("Failed to load alert settings");
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAlertSettings({
        email_alerts_enabled: emailEnabled,
        telegram_alerts_enabled: telegramEnabled,
        telegram_chat_id: telegramChatId,
        whatsapp_alerts_enabled: whatsappEnabled,
        whatsapp_number: whatsappNumber,
      });
      toast.success("Settings saved successfully");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await testEmailAlert(testEmailAddress);
      toast.success(`Test email sent to ${testEmailAddress}!`);
    } catch {
      toast.error("Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!telegramChatId) { toast.error("Please enter a Chat ID first"); return; }
    setTestingTelegram(true);
    try {
      await testTelegramAlert(telegramChatId);
      toast.success("Test Telegram message sent!");
    } catch {
      toast.error("Failed to send test telegram. Check your Chat ID.");
    } finally {
      setTestingTelegram(false);
    }
  };

  const handleTestWhatsapp = async () => {
    if (!whatsappNumber) { toast.error("Please enter a WhatsApp number first"); return; }
    setTestingWhatsapp(true);
    try {
      await testWhatsappAlert(whatsappNumber);
      toast.success("Test WhatsApp message sent!");
    } catch {
      toast.error("Failed to send test WhatsApp message.");
    } finally {
      setTestingWhatsapp(false);
    }
  };

  if (loading) {
    return (
      <div className="alrt-loading">
        <div className="alrt-loading-icon">
          <Bell size={22} className="alrt-loading-bell" />
        </div>
        <p className="alrt-loading-text">Loading preferences…</p>
      </div>
    );
  }

  return (
    <div className="alrt-page">

      {/* Page Header */}
      <div className="alrt-page-header">
        <div className="alrt-page-header-left">
          <div className="alrt-page-icon">
            <ShieldAlert size={22} />
          </div>
          <div>
            <h1 className="alrt-page-title">Alert Configuration</h1>
            <p className="alrt-page-sub">
              Control how and when you receive critical intelligence alerts.
            </p>
          </div>
        </div>

        {/* Sticky Save button */}
        <div className="alrt-save-wrap">
          <button
            className="alrt-save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><Loader2 size={15} className="alrt-spin" /> Saving…</>
            ) : (
              <><Save size={15} /> Save Configuration</>
            )}
          </button>
        </div>
      </div>

      {/* Channel Cards */}
      <div className="alrt-grid">

        {/* ── Email ── */}
        <ChannelCard
          title="Email Notifications"
          subtitle="Weekly reports & critical alerts"
          enabled={emailEnabled}
          onToggle={setEmailEnabled}
          accentColor="#3b82f6"
          icon={<Mail size={20} />}
        >
          {/* Info box */}
          <div className="alrt-info-box" style={{ borderColor: "rgba(59,130,246,0.2)", background: "rgba(59,130,246,0.05)" }}>
            <Info size={15} style={{ color: "#3b82f6", flexShrink: 0, marginTop: 1 }} />
            <p className="alrt-info-text">
              Email alerts provide a detailed summary of detected threats. Keep this enabled for an audit trail.
            </p>
          </div>

          {/* Input + send */}
          <div className="alrt-field">
            <label className="alrt-label">Recipient Address</label>
            <div className="alrt-input-row">
              <StyledInput
                placeholder="name@company.com"
                value={testEmailAddress}
                onChange={setTestEmailAddress}
                type="email"
                icon={<Mail size={14} />}
              />
              <button
                className="alrt-send-btn alrt-send-blue"
                onClick={handleTestEmail}
                disabled={testingEmail || !emailEnabled}
                title="Send test email"
              >
                {testingEmail ? <Loader2 size={16} className="alrt-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="alrt-hint">The address where threat monitoring reports will be sent.</p>
          </div>
        </ChannelCard>

        {/* ── Telegram ── */}
        <ChannelCard
          title="Telegram Integration"
          subtitle="Real-time instant notifications"
          enabled={telegramEnabled}
          onToggle={setTelegramEnabled}
          accentColor="#6366F1"
          icon={<Send size={20} />}
        >
          {/* Input section */}
          <div className="alrt-field">
            <label className="alrt-label">Telegram Chat ID</label>
            <div className="alrt-input-row">
              <StyledInput
                placeholder="e.g. 123456789"
                value={telegramChatId}
                onChange={setTelegramChatId}
                mono
              />
              <button
                className="alrt-send-btn alrt-send-blue"
                onClick={handleTestTelegram}
                disabled={testingTelegram || !telegramEnabled}
                title="Send test message"
              >
                {testingTelegram ? <Loader2 size={16} className="alrt-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>

          {/* Setup instructions */}
          <div className="alrt-instructions">
            <div className="alrt-instructions-header">
              <Info size={14} style={{ color: "var(--color-warning)" }} />
              <span>Setup Instructions</span>
            </div>
            <div className="alrt-steps">
              <div className="alrt-step">
                <div className="alrt-step-num">1</div>
                <div className="alrt-step-body">
                  <p className="alrt-step-title">Open the Bot</p>
                  <a
                    href="https://t.me/DarkWebMonitorBot"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="alrt-step-link"
                  >
                    @DarkWebMonitorBot <Send size={10} />
                  </a>
                </div>
              </div>
              <div className="alrt-step">
                <div className="alrt-step-num">2</div>
                <div className="alrt-step-body">
                  <p className="alrt-step-title">Start &amp; Copy ID</p>
                  <p className="alrt-step-desc">Click <strong>Start</strong> and copy the numerical ID sent to you.</p>
                </div>
              </div>
              <div className="alrt-step">
                <div className="alrt-step-num alrt-step-num-done">✓</div>
                <div className="alrt-step-body">
                  <p className="alrt-step-title">Save &amp; Verify</p>
                  <p className="alrt-step-desc">Paste the ID above, save changes, then click the test button.</p>
                </div>
              </div>
            </div>
          </div>
        </ChannelCard>

        {/* ── WhatsApp ── */}
        <ChannelCard
          title="WhatsApp Alerts"
          subtitle="Direct mobile notifications"
          enabled={whatsappEnabled}
          onToggle={setWhatsappEnabled}
          accentColor="#22c55e"
          icon={<MessageCircle size={20} />}
        >
          {/* Info box */}
          <div className="alrt-info-box" style={{ borderColor: "rgba(34,197,94,0.2)", background: "rgba(34,197,94,0.05)" }}>
            <Info size={15} style={{ color: "#22c55e", flexShrink: 0, marginTop: 1 }} />
            <p className="alrt-info-text">
              Receive instant alerts on WhatsApp. Ensure you have authorized our business number.
            </p>
          </div>

          {/* Input + send */}
          <div className="alrt-field">
            <label className="alrt-label">WhatsApp Number</label>
            <div className="alrt-input-row">
              <StyledInput
                placeholder="+1 234 567 890"
                value={whatsappNumber}
                onChange={setWhatsappNumber}
                icon={<Phone size={14} />}
                mono
              />
              <button
                className="alrt-send-btn alrt-send-green"
                onClick={handleTestWhatsapp}
                disabled={testingWhatsapp || !whatsappEnabled}
                title="Send test message"
              >
                {testingWhatsapp ? <Loader2 size={16} className="alrt-spin" /> : <Send size={16} />}
              </button>
            </div>
            <p className="alrt-hint">International format, e.g. +1 555 123 4567</p>
          </div>
        </ChannelCard>
      </div>
    </div>
  );
}
